import type { MatchResult } from '../types';

function analysisSemanticPhrase(match: MatchResult): string | null {
  const raw = match.analysis_json;
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  for (const k of ['semantic_rationale', 'semantic_summary', 'semantic_phrase']) {
    const v = o[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return null;
}

/**
 * Phase 7 §2.5: three labeled reason rows — skills, semantic signal, dimension scores.
 */
export function MatchCardTopReasons({ match: m }: { match: MatchResult }) {
  const skills = (m.matched_skills ?? []).filter(Boolean);
  const skillsLine = skills.length > 0 ? skills.slice(0, 3).join(', ') : '—';

  const sem = m.dimension_scores?.semantic_similarity;
  const semRounded = sem != null && !Number.isNaN(sem) ? Math.round(sem) : null;
  const fromAnalysis = analysisSemanticPhrase(m);
  const semanticLine =
    fromAnalysis ??
    (m.key_strengths?.[0]?.trim() || null) ??
    (semRounded != null ? `Resume–JD semantic alignment ${semRounded}/100` : '—');

  const ds = m.dimension_scores;
  let dimensionsLine = '—';
  if (ds) {
    dimensionsLine = [
      `Skills ${Math.round(ds.skill_overlap)}`,
      `Semantic ${Math.round(ds.semantic_similarity)}`,
      `Experience ${Math.round(ds.experience_alignment)}`,
    ].join(' · ');
  }

  const rowCls =
    'grid gap-x-3 gap-y-0.5 [grid-template-columns:minmax(4.5rem,5.5rem)_minmax(0,1fr)] text-[11px] leading-snug';

  return (
    <div className="space-y-2" aria-label="Top match reasons">
      <div className={rowCls}>
        <span className="font-semibold uppercase tracking-wide" style={{ color: 'var(--vscode-text-tertiary)' }}>
          Skills
        </span>
        <span className="font-medium min-w-0 break-words" style={{ color: 'var(--vscode-text-primary)' }}>
          {skillsLine}
        </span>
      </div>
      <div className={rowCls}>
        <span className="font-semibold uppercase tracking-wide" style={{ color: 'var(--vscode-text-tertiary)' }}>
          Semantic
        </span>
        <span className="font-medium min-w-0 break-words" style={{ color: 'var(--vscode-text-primary)' }}>
          {semanticLine}
        </span>
      </div>
      <div className={rowCls}>
        <span className="font-semibold uppercase tracking-wide" style={{ color: 'var(--vscode-text-tertiary)' }}>
          Scores
        </span>
        <span className="font-medium tabular-nums min-w-0 break-words" style={{ color: 'var(--vscode-text-primary)' }}>
          {dimensionsLine}
        </span>
      </div>
    </div>
  );
}
